import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Building2, Palette, Users, UserCog, ListTree, Link2, Mail, FileText, Package,
  Bell, CalendarDays, ShieldCheck, Plug, Server, ChevronRight, CheckCircle2, DollarSign,
} from "lucide-react";

const CARDS = [
  { key: "company", title: "Company", desc: "Business details, ABN/ACN, directors, banking.", icon: Building2, to: "/settings/company" },
  { key: "financial", title: "Financial Settings", desc: "Labour rates, overhead recovery, targets & job costing.", icon: DollarSign, to: "/settings/financial" },
  { key: "branding", title: "Branding", desc: "Logos, colours, company identity & letterhead.", icon: Palette, to: "/settings/branding" },
  { key: "users", title: "Users & Permissions", desc: "Manage users, roles and access.", icon: Users, to: "/employees" },
  { key: "customer-defaults", title: "Customer Defaults", desc: "Types, payment terms & billing defaults.", icon: UserCog, soon: true },
  { key: "price-list", title: "Price List", desc: "Master catalogue: charge codes, rates, units & minimums.", icon: ListTree, to: "/price-list" },
  { key: "materials", title: "Materials", desc: "Material cost catalogue feeding job profitability.", icon: Package, to: "/materials" },
  { key: "xero", title: "Xero Integration", desc: "Connect, sync & diagnostics.", icon: Link2, to: "/settings/xero", xero: true },
  { key: "integrations", title: "Integrations", desc: "Google Maps & Resend API keys — securely stored, test & manage.", icon: Plug, to: "/settings/integrations" },
  { key: "email", title: "Email & Communications", desc: "Edit every email template (invoices, reminders, quotes, onboarding, timesheets & more) with merge fields, preview & test send.", icon: Mail, to: "/settings/email" },
  { key: "documents", title: "Document Templates", desc: "Quote, invoice, docket & T&Cs.", icon: FileText, soon: true },
  { key: "notifications", title: "Notifications", desc: "Email, job & reminder preferences.", icon: Bell, soon: true },
  { key: "calendar", title: "Calendar", desc: "Public holidays, RDOs & business hours.", icon: CalendarDays, to: "/holidays-rdo" },
  { key: "security", title: "Security", desc: "MFA, sessions, audit & login history.", icon: ShieldCheck, soon: true },
  { key: "xero", title: "Integrations", desc: "Xero, Resend & future connections.", icon: Plug, to: "/settings/xero" },
  { key: "system", title: "System", desc: "Version, health, backups & deployment.", icon: Server, soon: true },
];

export default function SettingsHub() {
  const [xero, setXero] = useState(null);
  useEffect(() => { api.get("/xero/status").then((r) => setXero(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="settings-hub">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Settings</h1>
        <p className="text-slate-500 mt-1">Central administration hub — configure every part of True Seal from one place.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const connected = c.xero && xero?.connected;
          const inner = (
            <div className={`group bg-white rounded-2xl border border-slate-100 p-5 shadow-sm h-full transition-all ${c.soon ? "opacity-70" : "hover:shadow-md hover:border-navy/20 cursor-pointer"}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-navy/5 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-navy" />
                </div>
                {c.soon ? (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-100 px-2 py-1 rounded-full">Coming soon</span>
                ) : connected ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" data-testid="xero-card-connected"><CheckCircle2 className="w-3 h-3" />Connected</span>
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-navy transition-colors" />
                )}
              </div>
              <h3 className="font-heading font-semibold text-navy mt-4">{c.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
            </div>
          );
          return c.soon
            ? <div key={c.key} data-testid={`settings-card-${c.key}`}>{inner}</div>
            : <Link key={c.key} to={c.to} data-testid={`settings-card-${c.key}`}>{inner}</Link>;
        })}
      </div>
    </div>
  );
}
